import { GroupSecurity } from '@quromedical/fhir-common';
import { CareTeam } from '@quromedical/models';
import { CareTeamForm, InitialCareTeam } from 'core/care-team';
import { Alert, Section, Skeleton, SkeletonProvider, Snackbar } from 'design-system';
import { useRevalidation } from 'hooks/useRevalidation';
import { useUserSession } from 'hooks/useUserSession';
import { organizationFetcher } from 'integration';
import { CareTeamApi } from 'integration/aggregate';
import { CareTeamStackScreenProps } from 'navigation';
import React, { useCallback, useState } from 'react';
import { ScrollView } from 'react-native-gesture-handler';
import { strings } from 'strings';
import useSWR from 'swr';

const api = new CareTeamApi();

export const CareTeamScreen: React.FC<CareTeamStackScreenProps<'View'>> = ({ route }) => {
  const [hasError, setHasError] = useState(false);

  const { id } = route.params;
  const fetcher = useCallback(() => api.getCareTeam(id), [id]);
  const swrKey = `careTeam/${id}`;

  const { data, isValidating } = useSWR(swrKey, fetcher);

  const userSession = useUserSession();

  const canEdit = userSession.hasAny([GroupSecurity.CareTeamUpdate]);

  const orgFetcher = organizationFetcher();

  const revalidate = useRevalidation(id);

  const onAlertClose = useCallback(() => {
    setHasError(false);
  }, []);

  const loading = isValidating || !data;

  const initial = InitialCareTeam(data as CareTeam);

  return (
    <ScrollView>
      <SkeletonProvider loading={loading}>
        <Section hasBottomMargin hasTopMargin>
          <Skeleton>
            <CareTeamForm
              initial={initial}
              organizationFetcher={orgFetcher}
              canEdit={canEdit}
              onSubmit={revalidate}
            />
          </Skeleton>
          <Snackbar isOpen={hasError} onClose={onAlertClose}>
            <Alert
              actionIcon="close"
              backgroundColor="status-critical"
              title={strings.AlertError}
              onAction={onAlertClose}
              body={strings.CareTeamError}
            />
          </Snackbar>
        </Section>
      </SkeletonProvider>
    </ScrollView>
  );
};
